import { type } from '../utils/assist'

function isArray(arr) {
    return type(arr) == 'array'
}

function isObject(obj) {
    return type(obj) == 'object'
}

// 是否枚举元信息
function isEnumTuple(o) {
    // [ key, value, name, meta ]
    if (isArray(o)) {
        // 至少为[key, value]
        return o.length >= 2 && type(o[0]) == 'string'
    }

    // { key, value, name, meta }
    if (isObject(o)) {
        // 至少为 { key, value }
        return o.hasOwnProperty('key') && o.hasOwnProperty('value')
    }

    return false
}

// 是否枚举矩阵
function isMatrix(val) {
    return (
        (type(val) == 'array' && val.every((v) => isEnumTuple(v))) ||
        (type(val) == 'object' &&
            Object.keys(val).every((k) =>
                isEnumTuple([].concat(k).concat(val[k]))
            ))
    )
}

// 枚举定义标准化
function normalize(path, matrix, translate) {
    var normalized = []

    // map格式
    if (type(matrix) === 'object') {
        Object.keys(matrix).forEach((key) => {
            var options = {}

            options.key = key

            // { key: [ value, name ]}
            if (type(matrix[key]) == 'array') {
                options.value = matrix[key][0]
                options.name = matrix[key][1]
                options.meta = matrix[key][2]
            } else {
                // { key: value}
                options.value = matrix[key]
            }

            // 多语言翻译
            if (translate) {
                if (!options.name) {
                    options.name = translate(path + '.' + options.key) || ''
                }
            }

            normalized.push(options)
        })
    }

    // array格式 [enum1, enum2, ...]
    if (type(matrix) == 'array') {
        // [ [key, value, name ], { key, value, name, meta }, ... ]
        matrix.forEach((tuple) => {
            var options = {}

            // convert to { key, value, name, meta } object
            if (isArray(tuple)) {
                options = {
                    key: tuple[0],
                    value: tuple[1],
                    name: tuple[2],
                    meta: tuple[3],
                }
            } else {
                options = {
                    key: tuple.key,
                    value: tuple.value,
                    name: tuple.name,
                    meta: tuple.meta,
                }
            }

            // translate
            if (translate) {
                if (!options.name) {
                    options.name = translate(path + '.' + options.key) || ''
                }
            }

            normalized.push(options)
        })
    }

    return normalized
}

// 创建枚举类
var Enum = function (options) {
    this.init(options)
}

Object.assign(Enum.prototype, {
    init(options = {}) {
        this.options = Object.assign({}, Enum.options, options)

        // 定义属性
        this.key = this.options.key
        this.value = this.options.value
        this.name = this.options.name || this.key
        this.meta = this.options.meta

        // 封闭对象
        Object.seal(this)
    },

    toString() {
        return this.value
    },

    valueOf() {
        return this.value
    },

    toJSON() {
        return this.value
    },
})

// 枚举类工厂
function enumFactory(matrix) {
    // 创建枚举实例
    const enums = matrix.map((v) => new Enum(v))

    // 提供对外统一接口
    var singleEnum = {
        metas: {},
        names: {},
        enums: {},
        keys: {},
    }

    Object.assign(singleEnum, {
        // 枚举类转化为词典列表
        toList() {
            // copy 保证具备 id,text属性
            return enums.map((v) => ({
                ...v.options,
                id: v.value,
                text: v.name,
            }))
        },
        // 获取枚举对象
        getEnum(value) {
            var target = enums.find((e) => e.value == value)

            return target ? target.name : null
        },
        // 根据value获取meta
        getMeta(value) {
            var target = enums.find((e) => e.value == value)

            return target ? target.meta : null
        },
        // 根据value获取中文名称
        getName(value) {
            var target = enums.find((e) => e.value == value)

            return target ? target.name : value
        },
        // 根据value获取英文名称
        getKey(value) {
            var target = enums.find((e) => e.value == value)

            return target ? target.key : value
        },
        // key、value调换位置
        transpose() {
            var rvsMatrix = enums.map((v) => {
                var tuple = Object.assign({}, v.options)

                tuple.__temp__ = tuple.key
                tuple.key = tuple.value
                tuple.value = tuple.__temp__
                delete tuple.__temp__

                return tuple
            })

            return enumFactory(rvsMatrix)
        },
        // 支持遍历
        forEach(cb) {
            enums.forEach(cb)
        },
        // @depreciate alias to getName()
        displayNameOf(value) {
            return this.getName(value)
        },
        // @depreciate alias to getKey()
        keyOf(value) {
            return this.getKey(value)
        },
        // @depreciate alias to getKey()
        nameOf(value) {
            return this.getKey(value)
        },
    })

    // 添加静态枚举量
    enums.forEach((e) => {
        Object.freeze(e)

        singleEnum[e.key] = e.value
        singleEnum.metas[e.key] = e.meta
        singleEnum.names[e.key] = e.name
        singleEnum.keys[e.key] = e.key
        singleEnum.enums[e.key] = e
    })

    // enum flag
    singleEnum.__enum__ = true

    // 封闭枚举类
    return Object.freeze(singleEnum)
}

// 全局枚举
let cached = {},
    globalDefs

// manager
const enumManager = {
    // 获取全局枚举
    get(path) {
        if (cached[path]) {
            return cached[path]
        }

        var { globalEnum, basePath, translate } = enumManager.options,
            parts = path.split('.'),
            def = globalDefs || globalEnum,
            result = null

        // 支持函数式配置
        if (typeof def === 'function') {
            globalDefs = def = def(enumManager)
        }
        // obj-path
        for (var i = 0; i < parts.length; i++) {
            def = def[parts[i]]

            if (!def) break
        }
        // undef
        if (!def) {
            throw new Error(`enum '${path}' not defined`)
        }
        // enum instance
        if (def.__enum__ === true) {
            result = def
        } else {
            // enum options
            var normalMatrix = normalize(
                basePath ? basePath + '.' + path : path,
                def,
                translate // translate文本翻译
            )

            if (normalMatrix) {
                result = enumFactory(normalMatrix)
            }
        }
        cached[path] = result

        return result
    },
    // 创建一个枚举
    create(...args) {
        var path = '',
            matrix = null,
            { basePath, translate } = enumManager.options

        // [matrix]
        if (args.length == 1 && isMatrix(args[0])) {
            matrix = args[0]
        }

        // [enum1, enum2, enum3...]
        if (args.length > 1 && isEnumTuple(args[0])) {
            matrix = args
        }

        // [path, matrix]
        if (
            args.length == 2 &&
            type(args[0]) == 'string' &&
            isMatrix(args[1])
        ) {
            path = args[0]
            matrix = args[1]
        }

        // [path, enum1, enum2, enum3...]
        if (
            args.length > 1 &&
            type(args[0]) == 'string' &&
            isEnumTuple(args[1])
        ) {
            path = args[0]
            matrix = args.slice(1)
        }

        // args.length == 0 or sth else
        if (!matrix) {
            return null
        }

        // normalize
        var normalMatrix = normalize(
            basePath ? basePath + '.' + path : path,
            matrix,
            translate
        )

        return enumFactory(normalMatrix)
    },
    // 全局枚举
    options: {
        // 全局枚举
        globalEnum: {},
        // path前缀
        basePath: 'enum',
        // 多语言翻译函数
        translate: null,
    },
}

export default enumManager
